import { useState, useEffect, useRef } from 'react';
import { useSession } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Link, Plus, Trash, MagnifyingGlass, ArrowRight } from '@phosphor-icons/react';
import { RenoTicket, STATUS_COLORS } from '../types/renoTicket';

enum RenoTicketLinkType {
  RELATED = 'gerelateerd',
  BLOCKS = 'blokkeert',
  BLOCKED_BY = 'geblokkeerd_door',
  DUPLICATE = 'duplicaat',
}

interface TicketLink {
  id: number;
  sourceTicketId: number;
  targetTicketId: number;
  linkType: RenoTicketLinkType;
  createdAt: string;
  sourceTicket?: Partial<RenoTicket>;
  targetTicket?: Partial<RenoTicket>;
}

const LINK_TYPE_LABELS: Record<RenoTicketLinkType, string> = {
  [RenoTicketLinkType.RELATED]: 'Gerelateerd',
  [RenoTicketLinkType.BLOCKS]: 'Blokkeert',
  [RenoTicketLinkType.BLOCKED_BY]: 'Geblokkeerd door',
  [RenoTicketLinkType.DUPLICATE]: 'Duplicaat van',
};

const LINK_TYPE_COLORS: Record<RenoTicketLinkType, string> = {
  [RenoTicketLinkType.RELATED]: 'bg-blue-100 text-blue-700 border-blue-300',
  [RenoTicketLinkType.BLOCKS]: 'bg-red-100 text-red-700 border-red-300',
  [RenoTicketLinkType.BLOCKED_BY]: 'bg-orange-100 text-orange-700 border-orange-300',
  [RenoTicketLinkType.DUPLICATE]: 'bg-gray-100 text-gray-600 border-gray-300',
};

interface Props {
  ticketId: number;
}

export function RenoLinkedTicketsSection({ ticketId }: Props) {
  const { session } = useSession();
  const navigate = useNavigate();
  const [links, setLinks] = useState<TicketLink[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddOpen, setIsAddOpen] = useState(false);

  const fetchLinks = async () => {
    if (!session) return;
    setIsLoading(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/reno-helpdesk/tickets/${ticketId}/links`, { method: 'GET' }, session);
      setLinks(res.ok ? await res.json() : []);
    } catch {
      setLinks([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchLinks(); }, [ticketId, session]);

  const handleRemove = async (linkId: number) => {
    if (!session) return;
    await fetchWithAuth(`${API_URL}/reno-helpdesk/tickets/${ticketId}/links/${linkId}`, { method: 'DELETE' }, session);
    setLinks((prev) => prev.filter((l) => l.id !== linkId));
  };

  const getOtherTicket = (link: TicketLink) =>
    link.sourceTicketId === ticketId ? link.targetTicket : link.sourceTicket;

  const getOtherId = (link: TicketLink) =>
    link.sourceTicketId === ticketId ? link.targetTicketId : link.sourceTicketId;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Link size={14} className="text-blue-600" weight="duotone" />
          <span className="text-xs font-semibold text-gray-700">Gelinkte tickets ({links.length})</span>
        </div>
        <button
          onClick={() => setIsAddOpen(true)}
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 transition-colors"
        >
          <Plus size={12} />
          Link toevoegen
        </button>
      </div>

      {isLoading ? (
        <p className="text-xs text-gray-400">Laden...</p>
      ) : links.length === 0 ? (
        <p className="text-xs text-gray-400">Geen gelinkte tickets</p>
      ) : (
        <div className="space-y-2">
          {links.map((link) => {
            const other = getOtherTicket(link);
            const otherId = getOtherId(link);
            return (
              <div key={link.id} className="flex items-center gap-2 group">
                <span className={`shrink-0 inline-flex px-1.5 py-0.5 rounded border text-xs font-medium ${LINK_TYPE_COLORS[link.linkType]}`}>
                  {LINK_TYPE_LABELS[link.linkType]}
                </span>
                <button
                  onClick={() => navigate(`/renotickets/${otherId}`)}
                  className="flex-1 text-left text-xs text-gray-900 hover:underline truncate"
                >
                  <span className="font-mono text-gray-500 mr-1">RT-{otherId}</span>
                  {(other as any)?.title ?? ''}
                </button>
                {(other as any)?.status && STATUS_COLORS[(other as any).status as keyof typeof STATUS_COLORS] && (
                  <span className={`shrink-0 inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[(other as any).status as keyof typeof STATUS_COLORS]}`}>
                    {(other as any).status}
                  </span>
                )}
                <button
                  onClick={() => handleRemove(link.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-600"
                >
                  <Trash size={12} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <AddLinkModal
        isOpen={isAddOpen}
        ticketId={ticketId}
        onClose={() => setIsAddOpen(false)}
        onAdded={() => { setIsAddOpen(false); fetchLinks(); }}
      />
    </div>
  );
}

function AddLinkModal({ isOpen, ticketId, onClose, onAdded }: {
  isOpen: boolean;
  ticketId: number;
  onClose: () => void;
  onAdded: () => void;
}) {
  const { session } = useSession();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<RenoTicket[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [target, setTarget] = useState<RenoTicket | null>(null);
  const [linkType, setLinkType] = useState<RenoTicketLinkType>(RenoTicketLinkType.RELATED);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isOpen) { setSearch(''); setResults([]); setTarget(null); setLinkType(RenoTicketLinkType.RELATED); }
  }, [isOpen]);

  useEffect(() => {
    if (!search.trim() || !session) { setResults([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const params = new URLSearchParams({ search: search.trim(), limit: '10' });
        const res = await fetchWithAuth(`${API_URL}/reno-helpdesk/tickets?${params}`, { method: 'GET' }, session);
        const data = await res.json();
        setResults((data.tickets ?? []).filter((t: RenoTicket) => t.id !== ticketId));
      } catch { setResults([]); }
      finally { setIsSearching(false); }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search, session, ticketId]);

  const handleAdd = async () => {
    if (!target || !session) return;
    setIsSubmitting(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/reno-helpdesk/tickets/${ticketId}/links`,
        { method: 'POST', body: JSON.stringify({ targetId: target.id, linkType }), headers: { 'Content-Type': 'application/json' } },
        session,
      );
      if (!res.ok) throw new Error((await res.json()).message || 'Link toevoegen mislukt');
      onAdded();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Link toevoegen aan RT-{ticketId}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <Label className="text-xs">Type link</Label>
            <Select value={linkType} onValueChange={(v) => setLinkType(v as RenoTicketLinkType)}>
              <SelectTrigger className="text-xs h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(RenoTicketLinkType).map((t) => (
                  <SelectItem key={t} value={t} className="text-xs">{LINK_TYPE_LABELS[t]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!target ? (
            <div className="space-y-2">
              <Label className="text-xs">Zoek ticket</Label>
              <div className="relative">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Zoek op titel..."
                  className="pl-8 text-sm"
                  autoFocus
                />
              </div>
              {isSearching && <p className="text-xs text-gray-400">Zoeken...</p>}
              {results.length > 0 && (
                <div className="max-h-40 overflow-y-auto rounded-md border border-gray-200 divide-y divide-gray-100">
                  {results.map((t) => (
                    <button key={t.id} onClick={() => setTarget(t)} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2">
                      <span className="font-mono text-xs text-gray-500 shrink-0">RT-{t.id}</span>
                      <span className="font-medium text-gray-900 truncate flex-1">{t.title}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3 flex items-center gap-2">
              <span className="font-mono text-xs text-gray-500">RT-{target.id}</span>
              <ArrowRight size={12} className="text-gray-400 shrink-0" />
              <span className="text-sm font-medium text-gray-900 truncate flex-1">{target.title}</span>
              <button onClick={() => setTarget(null)} className="text-xs text-gray-400 underline shrink-0">Wijzigen</button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>Annuleren</Button>
          <Button onClick={handleAdd} disabled={!target || isSubmitting}>
            {isSubmitting ? 'Bezig...' : 'Link toevoegen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
